import { BODY_FONT_STACK, CSS_VAR_NAMES } from "@/components/design-tokens";
import { Sentry } from "@/instrument";
import { normalizeLegacyWidgetFontStack } from "@qa-app/core/widget-fonts";

/**
 * Inject base styles into the shadow DOM:
 *   - `:host` inherited-style isolation, font stack, text colour + font smoothing
 *   - Global `*, *::before, *::after { box-sizing: border-box }` reset
 *
 * Mirrors `tenant-ai-widget`'s `injectBaseStyles()`. New styled components no
 * longer need to declare `box-sizing: border-box` individually; the global
 * reset handles it.
 *
 * Historical note: this widget previously omitted the global reset because
 * pre-existing components had been sized to compensate for `content-box`
 * (padding/border added to width). The global reset was introduced as part of
 * the Wizen branding PR after evaluating the maintenance burden of remembering
 * per-component declarations. Any visual regressions surfaced after this
 * change should be fixed by adjusting the affected component's explicit
 * width/padding rather than reverting the reset.
 */
export const injectBaseStyles = (element: HTMLElement): void => {
  const shadowRoot = element.shadowRoot;
  if (!shadowRoot) {
    return;
  }

  // Check if styles already injected
  if (shadowRoot.querySelector("style[data-pollen-base-styles]")) {
    return;
  }

  const styleElement = document.createElement("style");
  styleElement.dataset.pollenBaseStyles = "true";
  styleElement.textContent = `
    :host {
      all: initial;
      direction: ltr;
      unicode-bidi: isolate;
      font-family: var(${CSS_VAR_NAMES.fontFamily}, ${BODY_FONT_STACK});
      color: var(${CSS_VAR_NAMES.textColor}, #223C72);
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      text-rendering: optimizeLegibility;
    }

    *, *::before, *::after {
      box-sizing: border-box;
    }
  `;
  shadowRoot.prepend(styleElement);
};

export type WidgetAttributes = {
  apiKey?: string;
  apiUrl?: string;
  disclaimerText?: string;
  suggestedQuestions?: string[];
  placeholderText?: string;
  textColor?: string;
  backgroundColor?: string;
  primaryColor?: string;
  cardBackgroundColor?: string;
  textSecondaryColor?: string;
  borderColor?: string;
  fontFamily?: string;
  headingFontFamily?: string;
  accentColor?: string;
  iconAccentColor?: string;
  inputBorderColor?: string;
  inputBackgroundColor?: string;
};

/**
 * Widget theme configuration — tenant-configurable colors and optional
 * font overrides from `/v2/widget-config`, plus input-chrome attribute overrides.
 */
export type WidgetTheme = {
  textColor?: string;
  backgroundColor?: string;
  primaryColor?: string;
  cardBackgroundColor?: string;
  textSecondaryColor?: string;
  borderColor?: string;
  fontFamily?: string;
  headingFontFamily?: string;
  accentColor?: string;
  iconAccentColor?: string;
  inputBorderColor?: string;
  inputBackgroundColor?: string;
};

export type WidgetThemeFetchError = {
  kind: "unauthorized" | "forbidden" | "http_error" | "network_error" | "invalid_response";
  status?: number;
  message: string;
};

export type WidgetThemeFetchResult = {
  theme: WidgetTheme | null;
  showChatQuestions: boolean;
  showLandingQuestions: boolean;
  disclaimerText?: string;
  suggestedQuestions?: string[];
  placeholderText?: string;
  organizationSlug?: string;
  langfuse?: {
    publicKey: string;
    baseUrl: string;
    environment?: string;
  } | null;
  error?: WidgetThemeFetchError;
};

// Keep track of values this runtime assigned so an API refresh can update its
// own inline values without clobbering a value supplied by the embedding host.
const widgetAssignedThemeVariables = new WeakMap<HTMLElement, Map<string, string>>();

const hasHostProvidedThemeVariable = (element: HTMLElement, variableName: string): boolean => {
  const inlineValue = element.style.getPropertyValue(variableName).trim();
  const assignedVariables = widgetAssignedThemeVariables.get(element);
  if (inlineValue) {
    if (assignedVariables?.get(variableName) === inlineValue) {
      return false;
    }
    assignedVariables?.delete(variableName);
    return true;
  }

  const computedValue = getComputedStyle(element).getPropertyValue(variableName).trim();
  if (computedValue) {
    assignedVariables?.delete(variableName);
    return true;
  }

  return false;
};

const setThemeVariable = (
  element: HTMLElement,
  variableName: string,
  value: string,
  overrideHostValue: boolean,
): void => {
  if (!overrideHostValue && hasHostProvidedThemeVariable(element, variableName)) {
    return;
  }

  element.style.setProperty(variableName, value);
  const assignedVariables = widgetAssignedThemeVariables.get(element) ?? new Map<string, string>();
  assignedVariables.set(variableName, element.style.getPropertyValue(variableName).trim());
  widgetAssignedThemeVariables.set(element, assignedVariables);
};

const normalizeHexColor = (value: string | undefined): string | undefined => {
  if (!value) {
    return undefined;
  }
  return value.startsWith("#") ? value : `#${value}`;
};

const parseSuggestedQuestions = (element: HTMLElement): string[] | undefined => {
  const questionsAttribute = element.getAttribute("questions");
  if (!questionsAttribute) {
    return undefined;
  }

  const questions = questionsAttribute
    .split(";")
    .map(question => question.trim())
    .filter(question => question.length > 0);

  return questions.length > 0 ? questions : undefined;
};

const readFontFamilyAttribute = (element: HTMLElement, name: string): string | undefined => {
  const value = element.getAttribute(name)?.trim();
  return value ? normalizeLegacyWidgetFontStack(value) : undefined;
};

export const readWidgetAttributes = (element: HTMLElement): WidgetAttributes => {
  return {
    apiKey: element.getAttribute("api-key")?.trim() ?? undefined,
    apiUrl: element.getAttribute("api-url")?.trim() ?? undefined,
    disclaimerText: element.getAttribute("disclaimer-text")?.trim() || undefined,
    suggestedQuestions: parseSuggestedQuestions(element),
    textColor: normalizeHexColor(element.getAttribute("text-color") ?? undefined),
    backgroundColor: normalizeHexColor(element.getAttribute("background-color") ?? undefined),
    primaryColor: normalizeHexColor(element.getAttribute("primary-color") ?? undefined),
    cardBackgroundColor: normalizeHexColor(element.getAttribute("card-background-color") ?? undefined),
    textSecondaryColor: normalizeHexColor(element.getAttribute("text-secondary-color") ?? undefined),
    borderColor: normalizeHexColor(element.getAttribute("border-color") ?? undefined),
    fontFamily: readFontFamilyAttribute(element, "font-family"),
    headingFontFamily: readFontFamilyAttribute(element, "heading-font-family"),
    accentColor: normalizeHexColor(element.getAttribute("accent-color") ?? undefined),
    iconAccentColor: normalizeHexColor(element.getAttribute("icon-accent-color") ?? undefined),
    // Border + background can be a hex from the picker OR an rgba() literal,
    // so they're passed through verbatim (no `#` prefix coercion).
    inputBorderColor: element.getAttribute("input-border-color")?.trim() || undefined,
    inputBackgroundColor: element.getAttribute("input-background-color")?.trim() || undefined,
  };
};

/**
 * Apply theme CSS variables to an element host (host-level so they cascade
 * into the shadow root).
 */
export const applyThemeVariables = (
  element: HTMLElement,
  theme: WidgetTheme,
  overrideHostValues = false,
) => {
  if (theme.textColor) {
    setThemeVariable(element, CSS_VAR_NAMES.textColor, theme.textColor, overrideHostValues);
  }
  if (theme.backgroundColor) {
    setThemeVariable(element, CSS_VAR_NAMES.backgroundColor, theme.backgroundColor, overrideHostValues);
  }
  if (theme.primaryColor) {
    setThemeVariable(element, CSS_VAR_NAMES.primaryColor, theme.primaryColor, overrideHostValues);
  }
  if (theme.cardBackgroundColor) {
    setThemeVariable(element, CSS_VAR_NAMES.cardBackgroundColor, theme.cardBackgroundColor, overrideHostValues);
  }
  if (theme.textSecondaryColor) {
    setThemeVariable(element, CSS_VAR_NAMES.textSecondaryColor, theme.textSecondaryColor, overrideHostValues);
  }
  if (theme.borderColor) {
    setThemeVariable(element, CSS_VAR_NAMES.borderColor, theme.borderColor, overrideHostValues);
  }
  if (theme.fontFamily) {
    setThemeVariable(element, CSS_VAR_NAMES.fontFamily, theme.fontFamily, overrideHostValues);
  }
  if (theme.headingFontFamily) {
    setThemeVariable(element, CSS_VAR_NAMES.headingFontFamily, theme.headingFontFamily, overrideHostValues);
  }
  if (theme.accentColor) {
    setThemeVariable(element, CSS_VAR_NAMES.accentColor, theme.accentColor, overrideHostValues);
  }
  if (theme.iconAccentColor) {
    setThemeVariable(element, CSS_VAR_NAMES.iconAccentColor, theme.iconAccentColor, overrideHostValues);
  }
  if (theme.inputBorderColor) {
    setThemeVariable(element, CSS_VAR_NAMES.inputBorderColor, theme.inputBorderColor, overrideHostValues);
  }
  if (theme.inputBackgroundColor) {
    setThemeVariable(element, CSS_VAR_NAMES.inputBackgroundColor, theme.inputBackgroundColor, overrideHostValues);
  }
};

type RecordValue = Record<string, unknown>;

type WidgetConfigWidget = RecordValue & {
  textColor?: string;
  backgroundColor?: string;
  primaryColor?: string;
  cardBackgroundColor?: string;
  textSecondaryColor?: string;
  borderColor?: string;
  accentColor?: string;
  iconAccentColor?: string;
  fontFamily?: string;
  headingFontFamily?: string;
  disclaimerText?: string;
  suggestedQuestions?: unknown;
  placeholderText?: string;
  bodyFont?: unknown;
  headingFont?: unknown;
  showChatQuestions?: unknown;
  showLandingQuestions?: unknown;
};

type LangfuseConfig = RecordValue & {
  publicKey?: string;
  baseUrl?: string;
  environment?: string;
};

type WidgetConfigResponse = RecordValue & {
  organizationSlug?: string;
  widget?: WidgetConfigWidget;
  langfuse?: LangfuseConfig | null;
};

const isRecord = (value: unknown): value is RecordValue =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const optionalStringFields = [
  "textColor",
  "backgroundColor",
  "primaryColor",
  "cardBackgroundColor",
  "textSecondaryColor",
  "borderColor",
  "accentColor",
  "iconAccentColor",
  "fontFamily",
  "headingFontFamily",
  "disclaimerText",
  "placeholderText",
] as const;

const isWidgetConfigResponse = (value: unknown): value is WidgetConfigResponse => {
  if (!isRecord(value)) {
    return false;
  }

  if (value.organizationSlug !== undefined && typeof value.organizationSlug !== "string") {
    return false;
  }

  if (value.widget !== undefined && !isRecord(value.widget)) {
    return false;
  }

  if (value.langfuse !== undefined && value.langfuse !== null && !isRecord(value.langfuse)) {
    return false;
  }

  if (value.widget) {
    const widget = value.widget;
    if (optionalStringFields.some(field =>
      widget[field] !== undefined && typeof widget[field] !== "string")) {
      return false;
    }
  }

  if (value.langfuse) {
    const langfuse = value.langfuse;
    if (["publicKey", "baseUrl", "environment"].some(field =>
      langfuse[field] !== undefined
      && typeof langfuse[field] !== "string")) {
      return false;
    }
  }

  return true;
};

type ResolvedRuntimeFont = {
  fontFamily: string;
  fontId?: string;
};

const parseResolvedRuntimeFont = (value: unknown): ResolvedRuntimeFont | null => {
  if (!isRecord(value) || typeof value.fontFamily !== "string") {
    return null;
  }

  const fontFamily = value.fontFamily.trim();
  if (!fontFamily) {
    return null;
  }

  const fontId = typeof value.fontId === "string" ? value.fontId.trim() : undefined;
  return { fontFamily, ...(fontId ? { fontId } : {}) };
};

// Module-level cache of in-flight + completed `/v2/widget-config` fetches.
//
// Each user-facing element (`pollen-drawer`, `pollen-search-box`,
// `pollen-landing-search-box`, `pollen-context-trigger`) independently runs
// `bootstrapWidgetWebComponent()` in its own `connectedCallback`. Embedded on
// the same host page with the same `api-key`/`api-url`, that produced 4
// simultaneous identical requests to `/v2/widget-config` per page load — and
// under rate limiting, 3 of the 4 could silently fail their bootstrap.
//
// Tenant config is stable for the page's lifetime, so we share both
// in-flight and resolved promises keyed by `${apiUrl}|${apiKey}`. AbortSignal
// passed in by individual callers cannot be propagated into the shared fetch
// (it would cancel everyone), so we race the shared promise against the
// caller's signal and report `AbortError` locally without poisoning the cache
// for other callers.
const widgetThemeCache = new Map<string, Promise<WidgetThemeFetchResult>>();

const buildCacheKey = (apiUrl: string, apiKey: string): string => `${apiUrl}|${apiKey}`;

export const __resetWidgetThemeCacheForTests = (): void => {
  widgetThemeCache.clear();
};

const fetchWidgetThemeUncached = async (apiUrl: string, apiKey: string): Promise<WidgetThemeFetchResult> => {
  try {
    const response = await fetch(`${apiUrl}/v2/widget-config`, {
      headers: {
        Authorization: `Bearer ${apiKey}`,
      },
    });

    if (!response.ok) {
      let kind: WidgetThemeFetchError["kind"] = "http_error";
      if (response.status === 401) {
        kind = "unauthorized";
      }
      else if (response.status === 403) {
        kind = "forbidden";
      }
      const error: WidgetThemeFetchError = {
        kind,
        status: response.status,
        message: `Theme request failed with status ${response.status}.`,
      };
      console.error("Pollen Drawer Widget: theme fetch failed.", {
        apiUrl,
        status: response.status,
      });
      // 401/403 are expected misconfigurations (bad API key, origin not allowed) and
      // stay console-only. Anything else (5xx, unexpected redirects, etc.) is worth
      // capturing in Sentry so on-call has visibility.
      if (kind === "http_error") {
        Sentry.captureMessage("Pollen Drawer Widget: theme fetch failed with unexpected status", {
          level: "error",
          tags: { component: "fetchWidgetTheme" },
          extra: { apiUrl, status: response.status },
        });
      }
      return { theme: null, showChatQuestions: true, showLandingQuestions: true, error };
    }

    const responseBody: unknown = await response.json();
    if (!isWidgetConfigResponse(responseBody)) {
      const error: WidgetThemeFetchError = {
        kind: "invalid_response",
        message: "Theme response was not valid JSON.",
      };
      console.error("Pollen Drawer Widget: theme fetch failed.", {
        apiUrl,
        reason: "invalid_response",
      });
      Sentry.captureMessage("Pollen Drawer Widget: theme response was not valid JSON", {
        level: "error",
        tags: { component: "fetchWidgetTheme" },
        extra: { apiUrl },
      });
      return { theme: null, showChatQuestions: true, showLandingQuestions: true, error };
    }

    const data = responseBody;
    const bodyFont = parseResolvedRuntimeFont(data.widget?.bodyFont);
    const headingFont = parseResolvedRuntimeFont(data.widget?.headingFont);

    return {
      theme: {
        textColor: normalizeHexColor(data.widget?.textColor),
        backgroundColor: normalizeHexColor(data.widget?.backgroundColor),
        primaryColor: normalizeHexColor(data.widget?.primaryColor),
        cardBackgroundColor: normalizeHexColor(data.widget?.cardBackgroundColor),
        textSecondaryColor: normalizeHexColor(data.widget?.textSecondaryColor),
        borderColor: normalizeHexColor(data.widget?.borderColor),
        fontFamily: bodyFont?.fontFamily ?? data.widget?.fontFamily,
        headingFontFamily: headingFont?.fontFamily ?? data.widget?.headingFontFamily,
        accentColor: normalizeHexColor(data.widget?.accentColor),
        iconAccentColor: normalizeHexColor(data.widget?.iconAccentColor),
      },
      showChatQuestions:
        typeof data.widget?.showChatQuestions === "boolean"
          ? data.widget.showChatQuestions
          : true,
      showLandingQuestions:
        typeof data.widget?.showLandingQuestions === "boolean"
          ? data.widget.showLandingQuestions
          : true,
      disclaimerText: typeof data.widget?.disclaimerText === "string" ? data.widget.disclaimerText : undefined,
      suggestedQuestions: Array.isArray(data.widget?.suggestedQuestions)
        ? data.widget.suggestedQuestions
            .filter(question => typeof question === "string")
            .map(question => question.trim())
            .filter(question => question.length > 0)
            .slice(0, 3)
        : undefined,
      placeholderText: typeof data.widget?.placeholderText === "string"
        ? data.widget.placeholderText
        : undefined,
      organizationSlug:
        typeof data.organizationSlug === "string" && data.organizationSlug.length > 0
          ? data.organizationSlug
          : undefined,
      langfuse:
        data.langfuse && typeof data.langfuse.publicKey === "string" && typeof data.langfuse.baseUrl === "string"
          ? {
              publicKey: data.langfuse.publicKey,
              baseUrl: data.langfuse.baseUrl,
              ...(typeof data.langfuse.environment === "string" && data.langfuse.environment.length > 0
                ? { environment: data.langfuse.environment }
                : {}),
            }
          : null,
    };
  }
  catch (error) {
    const result: WidgetThemeFetchError = {
      kind: "network_error",
      message: error instanceof Error ? error.message : "Network error.",
    };
    console.error("Pollen Drawer Widget: theme fetch failed.", {
      apiUrl,
      reason: "network_error",
    });
    Sentry.captureException(error, {
      tags: { component: "fetchWidgetTheme" },
      extra: { apiUrl },
    });
    return { theme: null, showChatQuestions: true, showLandingQuestions: true, error: result };
  }
};

/**
 * Cached entry point: every caller with the same `(apiUrl, apiKey)` pair
 * shares one in-flight `/v2/widget-config` fetch. Per-caller `AbortSignal`s
 * are honoured locally via `Promise.race` without interrupting the shared
 * request, so a context-trigger element that gets disconnected mid-bootstrap
 * doesn't drop the drawer's bootstrap with it.
 */
export const fetchWidgetTheme = (
  apiUrl: string,
  apiKey: string,
  signal?: AbortSignal,
): Promise<WidgetThemeFetchResult> => {
  const cacheKey = buildCacheKey(apiUrl, apiKey);
  let pending = widgetThemeCache.get(cacheKey);
  if (!pending) {
    pending = fetchWidgetThemeUncached(apiUrl, apiKey);
    widgetThemeCache.set(cacheKey, pending);
    void pending.then((result) => {
      if (result.error && widgetThemeCache.get(cacheKey) === pending) {
        widgetThemeCache.delete(cacheKey);
      }
    });
  }

  if (!signal) {
    return pending;
  }

  if (signal.aborted) {
    return Promise.resolve({ theme: null, showChatQuestions: true, showLandingQuestions: true });
  }

  return new Promise<WidgetThemeFetchResult>((resolve) => {
    const onAbort = () => resolve({ theme: null, showChatQuestions: true, showLandingQuestions: true });
    signal.addEventListener("abort", onAbort, { once: true });
    pending.then((result) => {
      signal.removeEventListener("abort", onAbort);
      if (!signal.aborted)
        resolve(result);
    });
  });
};

/**
 * Apply theme with attribute overrides.
 *
 * Priority order:
 * 1. HTML attribute overrides (highest)
 * 2. API-fetched theme
 * 3. CSS variable defaults in design-tokens.ts (fallback)
 */
export const applyThemeWithOverrides = (element: HTMLElement, theme: WidgetTheme | null, overrides: WidgetTheme) => {
  if (theme) {
    applyThemeVariables(element, theme);
    applyThemeVariables(element, overrides, true);
    return;
  }

  applyThemeVariables(element, overrides, true);
};
